import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import ProductImage from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import { usePrompt } from 'hooks/usePrompt';

import { ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

const AddSerialNumber = NiceModal.create(({ ID, orderID }) => {
	// Hooks and states declaration
	const { t } = useTranslation();
	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();

	const isMobile = useMediaQuery(Breakpoints.mobile);

	const [isLoading, setIsLoading] = useState(true);
	const [orderItems, setOrderItems] = useState(null);

	const handleCloseModal = () => {
		modal.remove();
	};

	// Form & Schema setup
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					orderItems: yup.array().of(
						yup.object().shape({
							serialNumber: yup.string().nullable()
						})
					)
				})
				.required(),
		[]
	);
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderItems: [
				{
					serialNumber: null
				}
			]
		}
	});

	const {
		handleSubmit,
		watch,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const onSubmit = async (data) => {
		await axiosPrivate
			.post(ORDER_ACTIONS.UPDATE_SERIAL_NUMBER, {
				ID,
				orderItems: data.orderItems.map((item) => ({
					serialNumber: item.serialNumber,
					ID: item.ID
				})),
				orderNumber: undefined
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListsInPage());
					handleCloseModal();
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	};

	const fillForm = useCallback(async () => {
		setIsLoading(true);

		await axiosPrivate
			.post(ORDER_ACTIONS.GET_SERIAL_NUMBER_DETAILS, {
				ID
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					setOrderItems(data.orderItems);
					reset({
						orderItems: data.orderItems.map((item) => ({
							...item
						})),
						orderID: data.orderID
					});
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});

		setIsLoading(false);
	}, [ID]);

	useEffect(() => {
		if (ID) {
			fillForm();
		}
	}, [ID]);

	const columns = useMemo(
		() => [
			{
				header: 'productName',
				renderCell: ({ row }) => (
					<ProductImage
						data={{
							name: row.original.name,
							imageURL: row.original.image
						}}
					/>
				),
				size: isMobile ? 100 : 150,
				showOnMobile: true
			},
			{
				header: 'sku',
				accessorKey: 'sku',
				size: 100
			},

			{
				header: 'serialNumber',
				accessorKey: 'serialNumber',
				renderCell: ({ row }) => <StringInput name={`orderItems[${row.index}].serialNumber`} />,
				size: isMobile ? 100 : 300,
				showOnMobile: true
			},
			{
				header: 'quantity',
				accessorKey: 'quantity',
				size: 100
			},
			{
				header: 'total',
				accessorKey: 'total',
				renderCell: ({ row }) => (
					<IntegerInput
						name={`orderItems[${row.index}].total`}
						unit={watch(`orderItems[${row.index}].currency`)}
						viewMode={'view'}
						allowFloat
					/>
				),
				size: 40
			}
		],
		[orderItems]
	);

	return (
		<Modal maxWidth="xx-large" fullwidth className="modal-addSerialNumber">
			<ModalTitle>
				<Typography variant="p1">
					{t('addSerialNumber')} (<strong>{orderID}</strong>)
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem>
						<Table columns={columns} data={orderItems} loading={isLoading} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" onClick={handleCloseModal} variant="outline" />
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faFloppyDisk}
					loading={isSubmitting}
					label="save"
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default AddSerialNumber;
